import config from "../../config";

const USE_BACKEND = true;

const EMPTY_FORM = {
    CompanyName: "", RegNo: "", TaxNo: "",
    Address: "", PhoneNumber: "", Email: "",
    Status: "Inactive", LogoUrl: "",
};

/* Get user from localStorage */
const getUser = () => JSON.parse(localStorage.getItem('user') || '{}');
const getAccountId = () => {
    const user = getUser();
    return user.id || user.accountId;
};

/**
 * Extracts and transforms profile data from server response
 */
function transformProfile(data) {
    if (data.LogoUrl && data.LogoUrl.startsWith('/')) {
        return { ...data, LogoUrl: `${config.apiUrl}${data.LogoUrl}` };
    }
    return data;
}

/**
 * Strips the apiUrl prefix for db storage
 */
function stripApiUrl(logoUrl) {
    if (!logoUrl) return "";
    return logoUrl.replace(config.apiUrl, "");
}

async function getProfile() {
    if (!USE_BACKEND) {
        return {
            CompanyName: "",
            RegNo: "", TaxNo: "",
            Address: "",
            PhoneNumber: "",
            Email: "",
            Status: "Active", LogoUrl: "",
            metrics: { customers: 0, suppliers: 0, invoices: 0, purchaseOrders: 0 }
        };
    }

    const accountId = getAccountId();
    const res = await fetch(`${config.apiUrl}/getCompanyProfile?account_id=${accountId}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();

    // Merge with EMPTY_FORM to ensure all fields exist for first-time use
    return transformProfile({ ...EMPTY_FORM, ...data });
}

async function uploadLogo(file) {
    const fd = new FormData();
    fd.append("files", file);
    const up = await fetch(`${config.apiUrl}/uploadCompanyLogo`, { method: 'POST', body: fd });
    if (!up.ok) throw new Error("Upload failed");
    const res = await up.json();
    return transformProfile(res); // returns { url: "..." } or similar
}

async function updateProfile(profile, logoFile) {
    if (!USE_BACKEND) {
        await new Promise(r => setTimeout(r, 600));
        return profile;
    }

    let logoUrl = profile.LogoUrl;

    // 1. Upload logo if new file provided
    if (logoFile) {
        const uploadRes = await uploadLogo(logoFile);
        logoUrl = uploadRes.LogoUrl || logoUrl;
    }

    // 2. Prepare payload (strip API URL prefix for DB)
    const accountId = getAccountId();
    const savePayload = {
        ...profile,
        LogoUrl: stripApiUrl(logoUrl)
    };

    // 3. Update profile
    const res = await fetch(`${config.apiUrl}/updateCompanyProfile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ account_id: accountId, ...savePayload }),
    });

    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    return transformProfile({ ...profile, LogoUrl: logoUrl });
}

async function getMetrics() {
    const accountId = getAccountId();

    try {
        const [custRes, suppRes, invRes] = await Promise.all([
            fetch(`${config.apiUrl}/getCustomerKpis?account_id=${accountId}`),
            fetch(`${config.apiUrl}/getSupplierKpis?account_id=${accountId}`),
            fetch(`${config.apiUrl}/getInvoiceKpis?account_id=${accountId}`),
        ]);

        const custKpi = custRes.ok ? await custRes.json() : { total: 0 };
        const suppKpi = suppRes.ok ? await suppRes.json() : { total: 0 };
        const invKpi = invRes.ok ? await invRes.json() : { total: 0 };

        return {
            customers: custKpi.total ?? 0,
            suppliers: suppKpi.total ?? 0,
            invoices: invKpi.total ?? 0,
            purchaseOrders: 0,
        };
    } catch {
        return { customers: 0, suppliers: 0, invoices: 0, purchaseOrders: 0 };
    }
}

export const companyProfileService = {
    getProfile,
    updateProfile,
    getMetrics
};